// ExternalAnalytics.tsx - FRONTEND SIN LÍMITES NI TIMEOUTS
import React, { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Statistic, Alert, Typography, Space, Tag, Table, Skeleton, Button } from "antd";
import { 
  NumberOutlined, 
  UserOutlined, 
  LoadingOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  ReloadOutlined
} from "@ant-design/icons";
import { API_BASE_URL } from "../../../constants/config";
import { getAndSanitize } from "../../../shared/utils/sanitization";
import { useAnalytics } from "../../../contexts/AnalyticsContext";
import { ExternalAnalyticsTrendChart } from "../components/charts/ExternalAnalyticsTrendChart";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;

interface AdminStats {
  adminId: string;
  adminUsername: string;
  adminRole: string;
  numeros: number;
  usuarios: number;
  cargas: number;
  userIds: string[];
}

interface ExternalAnalyticsData {
  date: string;
  timezone: string;
  stats: {
    numeros: number;
    usuarios: number;
    cargas: number;
  };
  adminStats: AdminStats[];
  details: {
    uniqueExternalUserIds: number;
    totalPanelUserIds: number;
    totalAdmins: number;
  };
}

interface ExternalAnalyticsProps {
  loading?: boolean;
}

export const ExternalAnalytics: React.FC<ExternalAnalyticsProps> = ({ loading: propLoading }) => {
  const [data, setData] = useState<ExternalAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const { filters } = useAnalytics();

  const fetchExternalAnalytics = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getAndSanitize("token");
      if (!token) {
        throw new Error("Token no disponible");
      }


      const response = await fetch(`${API_BASE_URL}/api/external-analytics/daily-stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setData(result.data);
        setLastUpdated(new Date());
      } else {
        throw new Error(result.message || "Error al obtener datos");
      }
    } catch (err: any) {
      console.error(`[ExternalAnalytics] Error fetching daily stats:`, err);
      setError(err.message || "Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRetry = useCallback(() => {
    fetchExternalAnalytics();
  }, [fetchExternalAnalytics]);

  useEffect(() => {
    fetchExternalAnalytics();
    
    // Actualizar cada 30 segundos
    const interval = setInterval(fetchExternalAnalytics, 30000);
    
    return () => clearInterval(interval);
  }, [fetchExternalAnalytics]);

  const formatDate = (dateString: any) => {
    if (!dateString || dateString === null || dateString === undefined) return '-';
    
    // Verificar si es un objeto vacío
    if (typeof dateString === 'object' && Object.keys(dateString).length === 0) return '-';
    
    // Verificar si es una cadena vacía
    if (typeof dateString === 'string' && dateString.trim() === '') return '-';
    
    try {
      const parsedDate = dayjs(dateString).tz("America/Argentina/Buenos_Aires");
      if (!parsedDate.isValid()) return '-';
      return parsedDate.format("DD/MM/YYYY");
    } catch {
      return '-';
    }
  };

  const currentTime = dayjs().tz("America/Argentina/Buenos_Aires").format("HH:mm:ss");

  if (loading || propLoading) {
    return (
      <div style={{ padding: '16px' }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {[1, 2, 3].map(i => (
            <Col xs={24} sm={12} md={8} key={i}>
              <Card size="small">
                <Skeleton active paragraph={{ rows: 2 }} />
              </Card>
            </Col>
          ))}
        </Row>
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error al cargar datos"
        description={error}
        type="error"
        showIcon
        action={
          <Button 
            onClick={handleRetry}
            icon={<ReloadOutlined />}
            size="small"
            type="primary"
            danger
            disabled={loading}
          >
            Reintentar
          </Button>
        }
      />
    );
  }

  if (!data) {
    return (
      <Alert
        message="Sin datos disponibles"
        description="No se encontraron datos de External Analytics para mostrar."
        type="info"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: "0" }}>
      {/* Header */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={3} style={{ margin: 0 }}>
              External Analytics - Estadísticas del Día
            </Title>
            <Space>
              <Tag icon={<CalendarOutlined />} color="blue">
                {formatDate(data.date)}
              </Tag>
              <Tag icon={<ClockCircleOutlined />} color="green">
                {currentTime} (Buenos Aires)
              </Tag>
            </Space>
          </div>
          <Text type="secondary">
            Última actualización: {lastUpdated.toLocaleTimeString()} | 
            Zona horaria: {data.timezone}
          </Text>
        </Space>
      </Card>

      {/* Estadísticas principales */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Números"
              value={data.stats.numeros}
              prefix={<NumberOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: "#1890ff" }}
              suffix="registros"
            />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Total de accesos registrados hoy
            </Text>
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Usuarios"
              value={data.stats.usuarios}
              prefix={<UserOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
              suffix="nuevos"
            />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Usuarios PanelUser creados hoy
            </Text>
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Cargas"
              value={data.stats.cargas}
              prefix={<LoadingOutlined style={{ color: "#fa8c16" }} />}
              valueStyle={{ color: "#fa8c16" }}
              suffix="completadas"
            />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              LoadRequests completados hoy
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Tabla de estadísticas por AdminId */}
      {data.adminStats && data.adminStats.length > 0 && (
        <Card 
          title={
            <Space>
              <TeamOutlined style={{ color: "#1890ff" }} />
              <span>Estadísticas por Admin ID</span>
              <Tag color="blue">{data.adminStats.length} Total</Tag>
              <Tag color="green">
                {data.adminStats.filter(admin => admin.numeros > 0 || admin.usuarios > 0 || admin.cargas > 0).length} Activos
              </Tag>
              <Tag color="default">
                {data.adminStats.filter(admin => admin.numeros === 0 && admin.usuarios === 0 && admin.cargas === 0).length} Inactivos
              </Tag>
            </Space>
          }
          style={{ marginBottom: 16 }}
          size="small"
        >
          <Table
            dataSource={data.adminStats.sort((a, b) => {
              const aTotal = a.numeros + a.usuarios + a.cargas;
              const bTotal = b.numeros + b.usuarios + b.cargas;
              if (aTotal === 0 && bTotal === 0) return a.adminUsername.localeCompare(b.adminUsername);
              if (aTotal === 0) return 1;
              if (bTotal === 0) return -1;
              return bTotal - aTotal;
            })}
            rowKey="adminId"
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            rowClassName={(record: AdminStats) => {
              const isInactive = record.numeros === 0 && record.usuarios === 0 && record.cargas === 0;
              return isInactive ? 'inactive-admin-row' : 'active-admin-row';
            }}
            columns={[
              {
                title: 'Admin Info',
                key: 'adminInfo',
                width: 200,
                render: (record: AdminStats) => {
                  const isInactive = record.numeros === 0 && record.usuarios === 0 && record.cargas === 0;
                  return (
                    <Space direction="vertical" size={2}>
                      <Space size={4}>
                        <Text strong style={{ fontSize: '12px' }}>
                          {record.adminUsername}
                        </Text>
                        <Tag 
                          color={record.adminRole === 'SUPERADMIN' ? 'red' : 'blue'} 
                          style={{ fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}
                        >
                          {record.adminRole}
                        </Tag>
                      </Space>
                      <Text code style={{ fontSize: '10px', color: '#666' }}>
                        {record.adminId.substring(0, 12)}...
                      </Text>
                      {isInactive && (
                        <Tag color="default" style={{ fontSize: '9px', padding: '0 3px', lineHeight: '14px' }}>
                          Sin actividad
                        </Tag>
                      )}
                    </Space>
                  );
                },
              },
              {
                title: 'Números',
                dataIndex: 'numeros',
                key: 'numeros',
                width: 80,
                align: 'center' as const,
                render: (value: number) => (
                  <Tag 
                    color={value > 0 ? "blue" : "default"} 
                    style={{ 
                      margin: 0, 
                      minWidth: '40px', 
                      textAlign: 'center',
                      opacity: value > 0 ? 1 : 0.6
                    }}
                  >
                    {value}
                  </Tag>
                ),
              },
              {
                title: 'Usuarios',
                dataIndex: 'usuarios',
                key: 'usuarios',
                width: 80,
                align: 'center' as const,
                render: (value: number) => (
                  <Tag 
                    color={value > 0 ? "green" : "default"} 
                    style={{ 
                      margin: 0, 
                      minWidth: '40px', 
                      textAlign: 'center',
                      opacity: value > 0 ? 1 : 0.6
                    }}
                  >
                    {value}
                  </Tag>
                ),
              },
              {
                title: 'Cargas',
                dataIndex: 'cargas',
                key: 'cargas',
                width: 80,
                align: 'center' as const,
                render: (value: number) => (
                  <Tag 
                    color={value > 0 ? "orange" : "default"} 
                    style={{ 
                      margin: 0, 
                      minWidth: '40px', 
                      textAlign: 'center',
                      opacity: value > 0 ? 1 : 0.6
                    }}
                  >
                    {value}
                  </Tag>
                ),
              },
              {
                title: 'UserIds',
                dataIndex: 'userIds',
                key: 'userIds',
                width: 200,
                render: (userIds: string[]) => {
                  if (!userIds || userIds.length === 0) {
                    return (
                      <Tag color="default" style={{ fontSize: '10px', margin: '1px', padding: '0 4px', lineHeight: '16px', opacity: 0.6 }}>
                        Sin UserIds
                      </Tag>
                    );
                  }
                  
                  return (
                    <Space size={2} wrap>
                      {userIds.slice(0, 3).map((userId, index) => (
                        <Tag key={index} color="cyan" style={{ fontSize: '10px', margin: '1px', padding: '0 4px', lineHeight: '16px' }}>
                          {userId}
                        </Tag>
                      ))}
                      {userIds.length > 3 && (
                        <Tag style={{ fontSize: '10px', margin: '1px', padding: '0 4px', lineHeight: '16px' }}>
                          +{userIds.length - 3}
                        </Tag>
                      )}
                    </Space>
                  );
                },
              },
            ]}
          />
        </Card>
      )}

      {/* Detalles adicionales */}
      <Card title="Detalles Adicionales">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Statistic
              title="UserIds Únicos Externos"
              value={data.details.uniqueExternalUserIds}
              valueStyle={{ color: "#13c2c2" }}
            />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              UserIds únicos registrados en External Analytics hoy
            </Text>
          </Col>

          <Col xs={24} sm={8}>
            <Statistic
              title="Total Panel User IDs"
              value={data.details.totalPanelUserIds}
              valueStyle={{ color: "#722ed1" }}
            />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              IDs de PanelUser relacionados encontrados
            </Text>
          </Col>

          <Col xs={24} sm={8}>
            <Statistic
              title="Admins Involucrados"
              value={data.details.totalAdmins}
              valueStyle={{ color: "#fa541c" }}
            />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Número total de AdminIds con actividad
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Gráfico de tendencias SIN RESTRICCIONES */}
      <div style={{ marginTop: 24 }}>
        <ExternalAnalyticsTrendChart 
          dateRange={filters.dateRange.start && filters.dateRange.end ? [filters.dateRange.start, filters.dateRange.end] : null}
          adminId="ALL"
          loading={loading || propLoading}
        />
      </div>

      {/* Información del sistema */}
      <Card style={{ marginTop: 16 }} size="small">
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text strong>Información del Sistema:</Text>
          <Text type="secondary">
            • Los datos se actualizan automáticamente cada 30 segundos
          </Text>
          <Text type="secondary">
            • Las estadísticas se calculan desde el primer registro hasta la fecha actual sin límites
          </Text>
          <Text type="secondary">
            • Consultas optimizadas con procesamiento bulk para máximo rendimiento
          </Text>
          <Text type="secondary">
            • "Números" = Total de registros en /api/external-analytics/register
          </Text>
          <Text type="secondary">
            • "Usuarios" = PanelUsers creados que coinciden con userId de External Analytics
          </Text>
          <Text type="secondary">
            • "Cargas" = LoadRequests con status COMPLETED y type LOAD de usuarios relacionados
          </Text>
        </Space>
      </Card>
    </div>
  );
};

export default ExternalAnalytics;